import type { Vec2 } from '../../geo/types';

export interface CornerProbe {
  readonly point: Vec2;
  // Distance from the probe to the nearest edge of the triangle it was stepped into.
  readonly clearance: number;
}

// The incentre is the point of a triangle farthest from its edges. On the way to it from a corner, along
// the bisector, the distance to the corner's two edges grows in step and the third edge stays farther,
// so a probe that stops `reach` along the way, or at the incentre itself, keeps that share of the inradius.
export function probeInto(corner: Vec2, b: Vec2, c: Vec2, reach: number): CornerProbe {
  const [opposite, afterB, afterC] = [
    Math.hypot(c[0] - b[0], c[1] - b[1]),
    Math.hypot(corner[0] - c[0], corner[1] - c[1]),
    Math.hypot(b[0] - corner[0], b[1] - corner[1]),
  ];
  const perimeter = opposite + afterB + afterC;
  const incentre: Vec2 = [
    (opposite * corner[0] + afterB * b[0] + afterC * c[0]) / perimeter,
    (opposite * corner[1] + afterB * b[1] + afterC * c[1]) / perimeter,
  ];
  const toIncentre = Math.hypot(incentre[0] - corner[0], incentre[1] - corner[1]);
  if (!(toIncentre > 0)) return { point: corner, clearance: 0 };
  const inradius =
    Math.abs((b[0] - corner[0]) * (c[1] - corner[1]) - (c[0] - corner[0]) * (b[1] - corner[1])) /
    perimeter;
  const share = Math.min(reach, toIncentre) / toIncentre;
  const point: Vec2 = [
    corner[0] + (incentre[0] - corner[0]) * share,
    corner[1] + (incentre[1] - corner[1]) * share,
  ];
  return { point, clearance: inradius * share };
}

// For each wanted vertex, the probe into one of its triangles that lands farthest from that triangle's
// edges. A vertex on an outline, or in the middle of a flat sliver along one, is ambiguous to point
// queries; a step well into a triangle that has room is not.
export function clearestCornerProbes(
  pointAt: (v: number) => Vec2,
  indices: ArrayLike<number>,
  wanted: (v: number) => boolean,
  reach: number
): ReadonlyMap<number, CornerProbe> {
  const best = new Map<number, CornerProbe>();
  const consider = (v: number, corner: Vec2, b: Vec2, c: Vec2) => {
    const probe = probeInto(corner, b, c, reach);
    const known = best.get(v);
    if (!known || probe.clearance > known.clearance) best.set(v, probe);
  };
  for (let t = 0; t < indices.length; t += 3) {
    const [u, v, w] = [indices[t]!, indices[t + 1]!, indices[t + 2]!];
    const [wantU, wantV, wantW] = [wanted(u), wanted(v), wanted(w)];
    if (!wantU && !wantV && !wantW) continue;
    const [pu, pv, pw] = [pointAt(u), pointAt(v), pointAt(w)];
    if (wantU) consider(u, pu, pv, pw);
    if (wantV) consider(v, pv, pw, pu);
    if (wantW) consider(w, pw, pu, pv);
  }
  return best;
}
